import Grid2 from '@mui/material/Grid2';
import { Theme } from '@mui/system/createTheme';
import useMediaQuery from '@mui/system/useMediaQuery';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { Meta } from 'src/components/Meta';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Scroller } from 'src/components/Scroller';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { fetchGroup } from 'src/models/group/group';
import { Video } from 'src/models/video/_types';
import { fetchGroupVideosByGroupId } from 'src/models/video/video';
import { Filters, TagsFilter } from 'src/shared/Filters/Filters';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { GroupVideoCard } from './components/GroupVideoCard';
import { VideoDialog } from './components/VideoDialog';

const prefix = 'video_';

export const VideosPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { session } = useSession();

  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);

  // Check if we're in an iframe
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);

  const {
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({ paramPrefix: prefix });

  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const search = params.search;
  const page = Number(params.page || '1');
  const perPage = Number(params.perPage || '12');

  // Fetch group data
  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => fetchGroup({ id: groupId! }),
    enabled: !!groupId,
  });

  // Fetch group videos
  const fetchVideosOptions = {
    groupId: groupId!,
    search,
    page,
    perPage,
    tags: filterParams?.tags?.values,
    matchAllTags: filterParams?.tags?.matchAll,
  };

  const { data: videosData, isLoading: videosLoading } = useQuery({
    queryKey: [
      'publicGroupVideos',
      groupId,
      page,
      search,
      perPage,
      filterParams?.tags,
    ],
    queryFn: () => fetchGroupVideosByGroupId(fetchVideosOptions),
    enabled: !!groupId,
  });

  const videos = videosData?.groupVideos || [];
  const total = videosData?.total || 0;
  const isLoading = groupLoading || videosLoading;

  // Check if current user is group admin
  // const isGroupAdmin = group?.adminIds?.includes(user?.id) || false;

  // Define filters
  const filters = [
    {
      field: 'tags',
      label: 'Tags',
      Component: TagsFilter,
      type: 'video',
      props: {
        showLabelPrefix: true,
      },
    },
  ];
  const videoHeaders = (group?.videoHeaders || '').split(',');

  const handleVideoClick = useCallback(
    (video: Video) => {
      // Check if we're in an iframe - if so, post message to parent
      if (isInIframe) {
        const message = {
          action: 'show-video',
          name: video.name,
          url: video.embedUrl,
          fullDescription: video.description,
        };
        window.parent.postMessage(message, '*');
      } else {
        // Normal dialog behavior when not in iframe
        setSelectedVideo(video);
        setVideoDialogOpen(true);
      }
    },
    [isInIframe]
  );

  const handleVideoDialogClose = () => {
    setVideoDialogOpen(false);
    setSelectedVideo(null);
  };

  const handleVideoHeaderClick = useCallback(
    (tag: string) => {
      const currentTags = filterParams?.tags?.values || [];
      const isActive = currentTags.includes(tag);

      if (isActive) {
        // Remove tag if already active
        const newTags = currentTags.filter((t) => t !== tag);
        if (newTags.length === 0) {
          updateFilterParam('tags', null);
        } else {
          updateFilterParam('tags', { values: newTags, matchAll: false });
        }
      } else {
        // Add tag
        updateFilterParam('tags', { values: [tag], matchAll: false });
      }
    },
    [filterParams?.tags?.values, updateFilterParam]
  );

  if (isLoading && !videos.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Meta title={`${group?.name} Videos`} brand={session?.brand} />
      <Box sx={{ p: 3 }}>
        <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
          <SearchParamSearchTextField
            params={params}
            updateParam={updateParam}
            placeholder="Search Video Name"
            sx={{ width: smDown ? '100%' : '280px' }}
          />
        </SectionToolbar>

        {/* Video Headers - Quick Tag Filters */}

        <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
          <Scroller
            direction="row"
            spacing={2}
            hideScrollbar
            sx={{ mx: -2, px: 2, boxSizing: 'content-box' }}
          >
            <Filters
              filters={filters}
              params={params}
              updateParam={updateParam}
              updateFilterParam={updateFilterParam}
              getFilterParamValue={getFilterParamValue}
              resetFilterParams={resetFilterParams}
            />
            {videoHeaders &&
              Array.isArray(videoHeaders) &&
              videoHeaders.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {videoHeaders.map((header) => {
                    const isActive =
                      filterParams?.tags?.values?.includes(header) || false;
                    return (
                      <Chip
                        key={header}
                        label={header}
                        onClick={() => handleVideoHeaderClick(header)}
                        variant={isActive ? 'soft' : 'outlined'}
                        color={isActive ? 'primary' : 'default'}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              )}
          </Scroller>
        </SectionToolbar>

        {/* Videos Grid */}
        <Box sx={{ mb: 6 }}>
          {videos.length > 0 ? (
            <>
              <Grid2 container spacing={3} sx={{ mb: 3 }}>
                {videos.map((video) => (
                  <Grid2
                    key={video.id}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                      lg: 3,
                      xl: 2,
                    }}
                  >
                    <GroupVideoCard
                      video={video}
                      onVideoClick={handleVideoClick}
                    />
                  </Grid2>
                ))}
              </Grid2>
              <SearchParamsPagination
                total={total}
                itemsPerPage={perPage}
                params={params}
                updateParam={updateParam}
              />
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {search
                  ? `Your search "${search}" did not match any videos.`
                  : 'No videos available.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {/* Video Dialog - only render when not in iframe */}
      {!isInIframe && (
        <VideoDialog
          open={videoDialogOpen}
          video={selectedVideo}
          onClose={handleVideoDialogClose}
        />
      )}
    </>
  );
};
